import React, { useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import * as styles from './carousel.module.scss';
import { IconButton } from '@material-ui/core';
import { Icon, IconType } from '../icon/icon';
import { useIntl } from 'gatsby-plugin-react-intl';

interface ICarouselItemProps {
  children: React.ReactElement | string;
}
export const CarouselItem = ({ children }: ICarouselItemProps) => {
  return <div className={styles.carouselItem}>{children}</div>;
};

interface ICarouselProps {
  children: React.ReactElement[];
  withIndicators?: boolean;
}
export const Carousel = ({
  children,
  withIndicators = false,
}: ICarouselProps) => {
  const intl = useIntl();
  const [delay] = useState(300);
  const [activeIndex, setActiveIndex] = useState(0);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);

  useEffect(() => {
    if (!buttonsDisabled) {
      // timeout elapsed, nothing to do
      return;
    }

    // buttonsDisabled was changed to true, set back to false after delay
    const handle = setTimeout(() => {
      setButtonsDisabled(false);
    }, delay);
    return () => clearTimeout(handle);
  }, [buttonsDisabled, delay]);

  const updateIndex = (index: number) => {
    if (index < 0) {
      index = React.Children.count(children) - 1;
    } else if (index >= React.Children.count(children)) {
      index = 0;
    }

    setActiveIndex(index);
  };

  const handleClick = (index: number) => {
    if (buttonsDisabled) {
      return;
    }

    setButtonsDisabled(true);
    return updateIndex(index);
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => updateIndex(activeIndex + 1),
    onSwipedRight: () => updateIndex(activeIndex - 1),
  });

  return (
    <div {...handlers} className={styles.carousel}>
      <div className={styles.wrapper}>
        {children.length > 1 && (
          <IconButton
            size="small"
            className={styles.navigateLeft}
            onClick={() => {
              handleClick(activeIndex - 1);
            }}
            aria-label={intl.formatMessage({
              id: 'products_pages.carousel.previous',
            })}
          >
            <Icon className={styles.navIcon} name={IconType.CHEVRON_LEFT} />
          </IconButton>
        )}
        <div
          className={styles.inner}
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {React.Children.map(
            children,
            child => child && React.cloneElement(child)
          )}
        </div>
        {children.length > 1 && (
          <IconButton
            size="small"
            className={styles.navigateRight}
            onClick={() => {
              handleClick(activeIndex + 1);
            }}
            aria-label={intl.formatMessage({
              id: 'products_pages.carousel.next',
            })}
          >
            <Icon className={styles.navIcon} name={IconType.CHEVRON_RIGHT} />
          </IconButton>
        )}
      </div>
      {withIndicators && (
        <div className={styles.indicators}>
          {React.Children.map(
            children,
            (_child, index) =>
              children.length > 1 && (
                <IconButton
                  size="small"
                  className={`${index === activeIndex ? styles.active : ''}`}
                  onClick={() => {
                    updateIndex(index);
                  }}
                  aria-label={intl.formatMessage({
                    id: 'products_pages.carousel.choose_image_position',
                  })}
                />
              )
          )}
        </div>
      )}
    </div>
  );
};
